import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { seoFor, DISTANCES } from '../i18n/seoLanding';
import { localizeRoom } from '../i18n/roomsI18n';
import { useRooms } from '../hooks/useRooms';

const Th = ({ children }) => <th className="py-2 pr-4 font-medium">{children}</th>;
const Td = ({ children, strong }) => <td className={`py-2 pr-4 ${strong ? 'text-slate-800' : 'text-slate-700'}`}>{children}</td>;

const RoomsTable = ({ labels }) => {
  const { language, t } = useLanguage();
  const rooms = useRooms().map((r) => localizeRoom(r, language, t));
  return (
    <div className="mt-12 max-w-3xl">
      <h3 className="font-semibold text-slate-900 text-lg">{labels.roomsTable}</h3>
      <table className="mt-3 w-full text-sm" data-testid="seo-rooms-table">
        <thead><tr className="text-left text-slate-600 text-xs uppercase tracking-wider"><Th>{labels.room}</Th><Th>{labels.size}</Th><Th>{labels.guests}</Th><Th>{labels.from}</Th></tr></thead>
        <tbody className="divide-y divide-amber-100">
          {rooms.map((r) => (
            <tr key={r.beds24Id || r.id}><Td strong>{r.name}</Td><Td>{r.size}</Td><Td>{r.guests}</Td><Td>€{Math.round(r.price)} {labels.perNight}</Td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DistancesTable = ({ labels, attractions }) => (
  <div className="mt-12 max-w-3xl">
    <h3 className="font-semibold text-slate-900 text-lg">{labels.distances}</h3>
    <table className="mt-3 w-full text-sm" data-testid="seo-distances">
      <thead><tr className="text-left text-slate-600 text-xs uppercase tracking-wider"><Th>{labels.place}</Th><Th>{labels.distance}</Th><Th>{labels.drive}</Th></tr></thead>
      <tbody className="divide-y divide-amber-100">
        {DISTANCES.map(([key, km, min]) => (
          <tr key={key}><Td strong>{attractions[key]?.name || key}</Td><Td>{String(km).replace('.', ',')} km</Td><Td>{min} min</Td></tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Keyword-rich, human-readable landing copy for the current page + language — visible to guests and to search/AI crawlers.
const SeoLanding = ({ slug = 'home' }) => {
  const { language, t } = useLanguage();
  const { labels, page } = seoFor(language, slug);
  if (!page) return null;
  const attractions = t('explore.attractions') || {};

  return (
    <section className="bg-[#f5f1ea] border-t border-amber-100" aria-labelledby={`seo-${slug}-title`} data-testid={`seo-landing-${slug}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <p className="text-[11px] uppercase tracking-[0.3em] text-amber-800 mb-3">{labels.more}</p>
        <h2 id={`seo-${slug}-title`} className="font-serif text-2xl md:text-3xl text-slate-900 leading-snug max-w-4xl">{page.h1}</h2>
        <p className="mt-5 text-slate-700 text-base leading-relaxed max-w-4xl">{page.intro}</p>
        {page.sections.length > 0 && (
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {page.sections.map((s) => (
              <article key={s.h2}>
                <h3 className="font-semibold text-slate-900 text-lg leading-snug">{s.h2}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.p}</p>
              </article>
            ))}
          </div>
        )}
        {slug === 'rooms' && <RoomsTable labels={labels} />}
        {(slug === 'home' || slug === 'location') && <DistancesTable labels={labels} attractions={attractions} />}
      </div>
    </section>
  );
};

export default SeoLanding;
